import { Box, SxProps, Theme } from "@mui/material";
import { ReactElement, ReactNode, useMemo } from "react";

const PLACEMENT_MAP = {
  topLeft: "1 auto auto 1",
  top: "1 1 auto 1",
  topRight: "1 1 auto auto",
  left: "1 auto 1 1",
  right: "1 1 1 auto",
  bottomLeft: "auto auto 1 1",
  bottom: "auto 1 1 1",
  bottomRight: "auto 1 1 auto",
};

const SIZE_MAP = {
  small: 0.5,
  medium: 1,
  large: 2,
};

export type MediaTileAdornmentPlacement = keyof typeof PLACEMENT_MAP;
export type MediaTileAdornmentSize = keyof typeof SIZE_MAP;

export type MediaTileAdornmentProps = {
  className?: string;
  placement?: MediaTileAdornmentPlacement;
  size?: MediaTileAdornmentSize;
  sx?: SxProps<Theme>;
  zIndex?: number;
  children: ReactNode;
};

export const MediaTileAdornment = ({
  className,
  placement = "topLeft",
  size = "medium",
  sx: sxProps,
  zIndex = 1,
  children,
}: MediaTileAdornmentProps): ReactElement => {
  const insetTemplate = PLACEMENT_MAP[placement] || PLACEMENT_MAP.topLeft;
  const sizeValue = SIZE_MAP[size] || 1;

  const sx: SxProps<Theme> = useMemo(
    () => ({
      position: "absolute",
      inset: (theme) => insetTemplate.replace(/1/g, theme.spacing(sizeValue)),
      zIndex,
      ...sxProps,
    }),
    [insetTemplate, sizeValue, sxProps, zIndex],
  );

  const classes = [
    "MuiMediaTileAdornment-root",
    `MuiMediaTileAdornment-${placement}`,
    `MuiMediaTileAdornment-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box className={classes} sx={sx}>
      {children}
    </Box>
  );
};

export type MediaTileAdornmentContainerProps = {
  className?: string;
  sx?: SxProps<Theme>;
  Adornments: ReactNode;
  MediaTile: ReactNode;
};

export const MediaTileAdornmentContainer = ({
  className,
  sx,
  Adornments,
  MediaTile,
}: MediaTileAdornmentContainerProps): ReactElement => (
  <Box
    className={["MuiMediaTileAdornmentContainer-root", className].filter(Boolean).join(" ")}
    sx={{
      position: "relative",
      "&:hover .MuiMediaTile-root": { backgroundColor: "grey.200" },
      ...sx,
    }}
  >
    {MediaTile}
    {Adornments}
  </Box>
);
